import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Save, Layers, ArrowRight } from "lucide-react";
import { fetchPricingLevels, updatePricingLevels, fetchPricingApplications } from "@admin/services/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pill } from "@admin/components/Primitives";
import { ADMIN_BASE } from "@admin/constants/route";
import { OPEN_STATUSES } from "@admin/components/approvals/LevelApplicationsQueue";

const fmt2 = (paise) =>
  `₹${(Number(paise || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Mirror backend v3 math for the 15-min video base preview.
function computeRowPreview(feeRupees, marginRupees, discountPctBps, gstRateBps) {
  const fee = Math.round(Number(feeRupees || 0) * 100);
  const margin = Math.round(Number(marginRupees || 0) * 100);
  const preDiscount = fee + margin;
  const discountRaw = Math.round(preDiscount * (Number(discountPctBps || 0) / 100) / 100);
  const discount = Math.min(discountRaw, preDiscount);
  const lawyerShare = Math.floor(discount / 2);
  const advosetShare = discount - lawyerShare;
  const lawyerPost = fee - lawyerShare;
  const marginPost = margin - advosetShare;
  const taxable = lawyerPost + marginPost;
  const gst = Math.round((taxable * Number(gstRateBps || 1800)) / 10000);
  return {
    customerPriceBeforeDiscountPaise: preDiscount,
    platformDiscountPaise: discount,
    lawyerDiscountPaise: lawyerShare,
    advosetDiscountPaise: advosetShare,
    lawyerFeePostDiscountPaise: lawyerPost,
    advosetMarginPostDiscountPaise: marginPost,
    customerPricePostDiscountPaise: taxable,
    gstPaise: gst,
    totalCustomerPayablePaise: taxable + gst,
  };
}

export default function PricingLevelsPanel() {
  const [cfg, setCfg] = useState(null);
  const [marketFees, setMarketFees] = useState({});
  const [fees, setFees] = useState({});
  const [margins, setMargins] = useState({});
  const [discounts, setDiscounts] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const hydrate = (c) => {
    setCfg(c);
    const mk = {}; const f = {}; const m = {}; const d = {};
    const rows = c.customer_pricing?.length
      ? c.customer_pricing
      : Object.entries(c.level_fees_paise || {}).map(([lvl, fee]) => ({
          level: Number(lvl),
          lawyer_market_fee_paise: null,
          lawyer_fee_paise: Number(fee),
          advoset_margin_paise: (c.level_margins_paise || {})[lvl] || 0,
          platform_discount_pct_bps: (c.level_discount_pct_bps || {})[lvl] || 0,
        }));
    rows.forEach((row) => {
      const lvl = String(row.level);
      mk[lvl] = row.lawyer_market_fee_paise == null ? "" : row.lawyer_market_fee_paise / 100;
      f[lvl] = (row.lawyer_fee_paise ?? 0) / 100;
      m[lvl] = (row.advoset_margin_paise ?? 0) / 100;
      d[lvl] = (row.platform_discount_pct_bps || 0) / 100;
    });
    setMarketFees(mk);
    setFees(f);
    setMargins(m);
    setDiscounts(d);
    setDirty(false);
  };

  const formatError = (e) => {
    const detail = e?.response?.data?.detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
    if (typeof detail === "object" && detail != null) return JSON.stringify(detail);
    return detail || e?.message || "Unknown error";
  };

  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([
        fetchPricingLevels(),
        fetchPricingApplications(OPEN_STATUSES),
      ]);
      hydrate(c);
      setPendingCount((a.items || []).length);
    } catch (e) {
      console.error("PricingLevelsPanel load failed:", e?.response?.data || e);
      toast.error(formatError(e) || "Could not load pricing levels");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return null;

  const gstBps = cfg.gst_rate_bps || 1800;
  const backendRows = {};
  (cfg.customer_pricing || []).forEach((row) => { backendRows[String(row.level)] = row; });

  const saveTable = async () => {
    const marketFeesPayload = {}; const feesPayload = {}; const marginsPayload = {}; const discountsPayload = {};
    for (let i = 1; i <= 10; i += 1) {
      const lvl = String(i);
      const mk = marketFees[lvl];
      const f = Number(fees[lvl]);
      const m = Number(margins[lvl]);
      const disc = Number(discounts[lvl]);
      if (!Number.isFinite(f) || f < 1) return toast.error(`Level ${i}: lawyer fee must be a positive number`);
      if (!Number.isFinite(m) || m < 0) return toast.error(`Level ${i}: margin cannot be negative`);
      if (!Number.isFinite(disc) || disc < 0 || disc > 100) return toast.error(`Level ${i}: platform discount must be between 0% and 100%`);
      if (mk !== "" && mk != null && (!Number.isFinite(Number(mk)) || Number(mk) < 0)) {
        return toast.error(`Level ${i}: lawyer market fee must be a non-negative number`);
      }
      feesPayload[lvl] = Math.round(f);
      marginsPayload[lvl] = Math.round(m);
      discountsPayload[lvl] = Math.round(disc * 100);
      marketFeesPayload[lvl] = mk === "" || mk == null ? null : Math.round(Number(mk));
    }
    setSaving(true);
    try {
      const c = await updatePricingLevels({
        level_fees_rupees: feesPayload,
        level_margins_rupees: marginsPayload,
        level_discount_pct_bps: discountsPayload,
        level_market_fees_rupees: marketFeesPayload,
      });
      hydrate(c);
      toast.success("Lawyer Pricing Table saved successfully. Changes apply to new bookings only.");
    } catch (e) {
      console.error("PricingLevelsPanel save failed:", e?.response?.data || e);
      toast.error(formatError(e) || "Failed to save Lawyer Pricing Table.");
    }
    setSaving(false);
  };

  return (
    <section className="bg-white border border-slate-200 border-t-4 border-t-[#0A2540] rounded-2xl p-5 mt-8" data-testid="pricing-levels-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Customer pricing</p>
          <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            <Layers size={16} className="inline mr-1" /> Lawyer Pricing Levels — Customer Pricing Table
          </h2>
          <p className="text-xs text-slate-500">
            Base: <span className="font-bold">Video · 15 min</span>. Configure Lawyer Market Fee (MRP), Lawyer Fee, Advoset Margin and Platform Discount. The platform discount is split 50:50 between Lawyer Fee and Advoset Margin. GST (18%) is calculated on the post-discount customer price.
          </p>
        </div>
        <Pill color="blue">v{cfg.version}</Pill>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm" data-testid="pricing-table">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-400">
              <th className="px-3 py-2.5 font-bold sticky left-0 bg-slate-50 z-10">Level</th>
              <th className="px-3 py-2.5 font-bold min-w-[120px]">Lawyer Market Fee (MRP)<span className="block normal-case font-medium text-slate-400">15 min video · reference</span></th>
              <th className="px-3 py-2.5 font-bold min-w-[120px]">Lawyer Fee<span className="block normal-case font-medium text-slate-400">15 min video</span></th>
              <th className="px-3 py-2.5 font-bold min-w-[120px]">Advoset Margin</th>
              <th className="px-3 py-2.5 font-bold min-w-[130px]">Customer Price Before Discount<span className="block normal-case font-medium text-slate-400">Before GST</span></th>
              <th className="px-3 py-2.5 font-bold min-w-[110px] text-[#D4AF37]">Platform Discount (%)</th>
              <th className="px-3 py-2.5 font-bold min-w-[120px] text-emerald-700">Lawyer Fee (Post Discount)</th>
              <th className="px-3 py-2.5 font-bold min-w-[120px] text-emerald-700">Advoset Margin (Post Discount)</th>
              <th className="px-3 py-2.5 font-bold min-w-[130px]">Customer Price Post Discount<span className="block normal-case font-medium text-slate-400">Before GST</span></th>
              <th className="px-3 py-2.5 font-bold min-w-[120px]">GST on Customer Invoice<span className="block normal-case font-medium text-slate-400">18%</span></th>
              <th className="px-3 py-2.5 font-bold min-w-[140px] text-[#0A2540]">Total Customer Payable<span className="block normal-case font-medium text-slate-400">Incl. GST</span></th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((lvl) => {
              const row = backendRows[lvl];
              const preview = computeRowPreview(fees[lvl], margins[lvl], discounts[lvl] * 100, gstBps);
              return (
                <tr key={lvl} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`pricing-row-${lvl}`}>
                  <td className="px-3 py-2 font-bold text-[#0A2540] sticky left-0 bg-white z-10" data-testid={`level-name-${lvl}`}>Level {lvl}</td>
                  <td className="px-3 py-2">
                    <div className="relative w-28">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                      <Input type="number" min={0} value={marketFees[lvl] ?? ""} className="pl-6 h-8 text-sm"
                        onChange={(e) => { setMarketFees({ ...marketFees, [lvl]: e.target.value }); setDirty(true); }}
                        data-testid={`level-market-fee-${lvl}`} />
                    </div>
                    {(() => {
                      const mrpRupees = Number(marketFees[lvl] || 0);
                      const lawyerFeePostRupees = preview.lawyerFeePostDiscountPaise / 100;
                      if (!mrpRupees || mrpRupees <= 0) return null;
                      const showStrike = mrpRupees > lawyerFeePostRupees;
                      return (
                        <div className={`mt-1 text-[10px] font-medium tabular-nums ${showStrike ? "text-slate-400 line-through" : "text-slate-500"}`} data-testid={`level-mrp-preview-${lvl}`}>
                          MRP ₹{mrpRupees.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2">
                    <div className="relative w-28">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                      <Input type="number" min={1} value={fees[lvl] ?? ""} className="pl-6 h-8 text-sm"
                        onChange={(e) => { setFees({ ...fees, [lvl]: e.target.value }); setDirty(true); }}
                        data-testid={`level-fee-${lvl}`} />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="relative w-28">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                      <Input type="number" min={0} value={margins[lvl] ?? ""} className="pl-6 h-8 text-sm"
                        onChange={(e) => { setMargins({ ...margins, [lvl]: e.target.value }); setDirty(true); }}
                        data-testid={`level-margin-${lvl}`} />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-[#0A2540] tabular-nums" data-testid={`level-before-discount-${lvl}`}>
                    {fmt2(preview.customerPriceBeforeDiscountPaise)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="relative w-24">
                      <Input type="number" min={0} max={100} step={0.01}
                        value={discounts[lvl] ?? 0} className="pr-7 h-8 text-sm"
                        onChange={(e) => { setDiscounts({ ...discounts, [lvl]: e.target.value }); setDirty(true); }}
                        data-testid={`level-discount-${lvl}`} />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-700 tabular-nums" data-testid={`level-fee-post-${lvl}`}>
                    {fmt2(preview.lawyerFeePostDiscountPaise)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-700 tabular-nums" data-testid={`level-margin-post-${lvl}`}>
                    {fmt2(preview.advosetMarginPostDiscountPaise)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-[#0A2540] tabular-nums" data-testid={`level-after-discount-${lvl}`}>
                    {fmt2(preview.customerPricePostDiscountPaise)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-[#0A2540] tabular-nums" data-testid={`level-gst-${lvl}`}>
                    {fmt2(preview.gstPaise)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-[#0A2540] tabular-nums text-base" data-testid={`level-total-${lvl}`}>
                    {fmt2(preview.totalCustomerPayablePaise)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] text-slate-400 max-w-3xl">
          Lawyer Market Fee is an informational reference price only and never affects calculations. Platform Discount is applied to the pre-GST customer price and shared equally between Lawyer Fee and Advoset Margin. GST is calculated on the post-discount price. Existing bookings keep their frozen snapshots.
        </p>
        <Button onClick={saveTable} disabled={saving || !dirty} data-testid="save-pricing-table-btn" className="rounded-xl adv-btn-primary font-bold">
          <Save size={14} /> {saving ? "Saving…" : "Save Lawyer Pricing Table"}
        </Button>
      </div>

      <Link to={`${ADMIN_BASE}/approvals/levels`} data-testid="pricing-level-requests-link"
        className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-3 text-sm hover:border-[#0A2540] hover:bg-slate-50 transition-colors">
        <span className="text-slate-600">
          <span className="font-bold text-[#0A2540]" data-testid="pricing-level-requests-count">{pendingCount}</span>
          {" "}pending level {pendingCount === 1 ? "request" : "requests"} — decided under Approvals → Lawyer Level Approval
        </span>
        <ArrowRight size={16} className="text-[#0A2540] shrink-0" />
      </Link>
    </section>
  );
}
